// Prerendered `/community` — sharing tiers, groups, and the two use-cases. Prerender-safe + loader-free for the same reasons as series.tsx: this module's whole graph is eagerly imported in Node at prerender, so no store/Amplify/@shared/window here.
// Chrome (theme + nav + footer) comes from splash-chrome, which provides the ThemeProvider — this file must NOT also wrap `Mui`.
// Its <title> deliberately avoids "ai journal": that term belongs to `/` and two pages competing for it cannibalize each other. The phrase appears once, in hero body copy.
// Every user-facing string here is governed by the marketing ban list enforced by community.dom.test.tsx — clinical vocabulary is a regulatory risk on this page, not a style question. That regex is not the whole rule and `#couples` is where it fails: an OUTCOME claim ("watching each other grow") is an unsubstantiated benefit claim under FTC medical/health-claim scrutiny, and no ban word appears in it — say what the feature DOES, never what it does for the reader.
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MUILink from '@mui/material/Link';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
// MenuBookOutlined, not AutoStoriesOutlined: MUI bakes the icon's component name into a `data-testid` that survives the production build, so an "AutoStories" glyph puts the literal string "Stories" into the prerendered HTML — A13 says "Series" everywhere.
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import CasinoIcon from '@mui/icons-material/CasinoOutlined';
import Diversity3Icon from '@mui/icons-material/Diversity3Outlined';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { Band, SectionHeader, SectionBody, FeatureCard, CtaBand, accentColor } from './splash-sections';
import { metaDescription, splashMeta, ORIGIN, softwareApplicationLd } from './series-ld';
import type { Route } from './+types/community';

const TITLE = 'Private Note Sharing, Groups & Collaborative Series — Gnothi';
const DESCRIPTION = metaDescription(
  'Share selected notes at the privacy tier you pick, join anonymous groups, and choose when eligible shared context can contribute to a Series.',
);
const PATH = '/community';

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi Community',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'SocialNetworkingApplication',
      featureList: ['Per-share privacy tiers', 'Anonymous groups', 'Groups matched by what you write about', 'Shared entries woven into your Series'],
    }),
  },
];

// The hero's proof element: what the OTHER person actually gets at each depth, previewing the `Tiers` band below. Glosses track `Sharing/Outbound/Form/Form.tsx`'s own tier descriptions, so re-word them together. Rendered as plain `Box`, never a MUI `Chip` or `Card` — both paint their own `palette.text.primary` over the navy wash regardless of what you nest (`Card/Card.js`, `Chip/Chip.js:83`), which is the trap splash-sections' OnDark note describes.
const HERO_TIERS = [
  ['Full', 'your words, as you wrote them'],
  ['Summary', 'the gist, not the entry'],
  ['Sensitive', 'their AI only, never listed'],
] as const;

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <PeopleIcon sx={{ fontSize: 48 }} />
        <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
          Some stories are better with other people in them
        </Typography>
        <Typography variant="h6" component="p" sx={{ fontWeight: 400, maxWidth: 720, opacity: 0.9 }}>
          Share selected notes with one person, or join a group around a common subject. Each share has its own depth. A Series uses a shared note only when the share permits AI use and you include shared context.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 5 }} sx={{ justifyContent: 'center', flexWrap: 'wrap', pt: 1 }}>
          {HERO_TIERS.map(([tier, gloss]) => (
            <Box key={tier}>
              {/* `component="div"`: MUI maps `subtitle2` to a real <h6>, and these are labels, not headings — three of them here would land above every section's <h2> in the document outline. */}
              <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, letterSpacing: '0.04em' }}>{tier}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>{gloss}</Typography>
            </Box>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
          <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" size="large" sx={{ px: 4 }}>
            Get started free
          </Button>
          {/* A plain `href`, never `component={RouterLink} to="#groups"` — that bakes `href="/community/#groups"` and full-page-navigates. */}
          <Button component="a" href="#groups" variant="outlined" color="inherit" size="large">
            See how groups work
          </Button>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Full-entry sharing is free, and so is joining and posting in groups. Starting your own group and the AI-summary tiers are on the paid plan.
        </Typography>
      </Stack>
    </Band>
  );
}

function SharingSeries() {
  return (
    <Band>
      <SectionHeader
        icon={<MenuBookIcon />}
        tone="themes"
        eyebrow="Why share at all"
        title="What sharing can add to a Series"
        deck="A shared note arrives with its author and privacy depth intact. You decide whether your Series uses shared context."
      />
      <SectionBody>
        Every note someone shares with you arrives stamped with their name and reduced to the tier they chose. Their words remain attributed to them. A group membership by itself does not connect its posts to your Series.
      </SectionBody>
      <SectionBody>
        When an eligible shared note is included, a chapter can carry the other person's account alongside yours. You can also keep a Series completely separate from notes and shared context.{' '}
        <MUILink component={RouterLink} to="/series">This is where they show up →</MUILink>
      </SectionBody>
    </Band>
  );
}

// The price label in each tier card's `footer` action row. `component="div"` because MUI maps `subtitle2` to a real <h6>, which inside a FeatureCard would sit below that card's own <h3> in the document outline.
function Price({ paid = false, children }: { paid?: boolean; children: ReactNode }) {
  return <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, ...(paid ? null : { color: accentColor() }) }}>{children}</Typography>;
}

function Tiers() {
  return (
    <Band color="grey">
      <SectionHeader
        icon={<ShieldIcon />}
        tone="themes"
        title="Three privacy tiers, and you pick per share"
        deck="A share is scoped to the tags you pick, and every tier limits what the other person and their AI can receive."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Full" tone="themes" footer={<Price>Free</Price>}>
            They see the entry as you wrote it, and their AI can use it as context. Full sharing is free.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Summary" tone="themes" footer={<Price paid>Paid plan</Price>}>
            They see an AI summary in place of the entry. Their AI can use the summary, while your original text stays private.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Sensitive" tone="themes" footer={<Price paid>Paid plan</Price>}>
            Their AI can use a confidential summary, but the note never appears in their entries list. A Sensitive share goes only to a named person, never to a group.
          </FeatureCard>
        </Grid>
      </Grid>
      {/* `shares_delete_request` (services/routes/shares/index.ts) is UNGATED as of `dd4143a5c` while post/put gate on `requirePaidTier(privacyTier)`, and `Shares.delete` scopes to the owner's uid — so payment gates CREATING a summary/sensitive share, never revoking one. Do not extend this into any other claim about what the free tier gets. */}
      <SectionBody>
        Deleting a share revokes access immediately and removes its tag from the other person's tag bar. Revoking is always free. The paid plan applies only when you create or edit a Summary or Sensitive share.
      </SectionBody>
    </Band>
  );
}

function Groups() {
  return (
    <Band id="groups">
      <SectionHeader
        icon={<GroupsIcon />}
        tone="themes"
        title="Groups, anonymous by construction"
        deck="Each group gives you a separate petname. Other members never receive your real username."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FeatureCard title="A petname per group" tone="themes">
            Join as a name like <em>brave-fox-4821</em>. You get a different petname in each group, and other members never see your real username.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FeatureCard title="Roles, and a door" tone="themes">
            Owner, admin, member, banned. Private groups are invisible to non-members, and a non-member cannot read messages even in a public group.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FeatureCard title="Reviewed in the background" tone="themes">
            Every post gets a background model check. Abusive or spammy posts are removed from the room, including the poster's own view.
          </FeatureCard>
        </Grid>
      </Grid>
      {/* Browsing, joining and posting really are free (`#1840`): `Setup/Routing.tsx`'s `/g` subtree and mobile's groups screen are both ungated, and only the create CTA reads `hasPaidAccess`. Never re-broaden this to "Groups are paid". */}
      <SectionBody>
        Browsing, joining, and posting are free. The paid plan lets you start, invite people to, and run a group of your own.
      </SectionBody>
    </Band>
  );
}

function Matching() {
  return (
    <Band color="dark">
      <SectionHeader
        icon={<HubIcon />}
        tone="themes"
        title="Found by what you write about"
        deck="Gnothi looks for groups discussing subjects that resemble the ones in your own writing."
      />
      <SectionBody>
        Matching compares patterns in your writing with patterns in each group's members. A smaller group circling the same subject can appear before a busier group about something else.
      </SectionBody>
      <SectionBody>
        Matching <strong>improves as you write</strong>. On your first visit, or while Gnothi is still preparing a match, you see recently active groups instead. <strong>Matchable</strong> groups can appear in these suggestions, <strong>public</strong> groups are open to browse, and <strong>private</strong> groups remain invitation-only.
      </SectionBody>
    </Band>
  );
}

function Dnd() {
  return (
    <Band id="dnd">
      <SectionHeader
        icon={<CasinoIcon />}
        tone="themes"
        eyebrow="In practice"
        title="Turn campaign notes into a continuing Series"
        deck="Retell a completed session as a story, listen between games, and keep the source notes under your control."
      />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeatureCard title="Choose the campaign notes" tone="themes">
            Use your own tagged notes, include eligible perspectives that players shared with you, or start an original adventure from the campaign brief. Joining a group never adds anyone's writing to a Series.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeatureCard title="Read it or listen together" tone="themes">
            Generate a chapter from the sources you chose, then read it in Gnothi or listen to its narration. If you enable a Feed URL, anyone with that URL can add the show to a podcast app.
          </FeatureCard>
        </Grid>
      </Grid>
      <SectionBody>
        <MUILink component={RouterLink} to="/dnd">See the campaign setup and an illustrative scene →</MUILink>{' · '}
        <MUILink component={RouterLink} to="/publishing#subscribe">Learn how to use a Feed URL →</MUILink>
      </SectionBody>
    </Band>
  );
}

function Couples() {
  return (
    <Band id="couples" color="grey">
      <SectionHeader
        icon={<Diversity3Icon />}
        tone="themes"
        eyebrow="In practice"
        title="Two people, one shared thread"
        deck="Mutual sharing, each side choosing its own tags and its own tier."
      />
      <SectionBody>
        Partners can share notes with each other, each choosing which tags to share and at which tier. What you each write stays attributed to you; the other person receives only what the share includes, and either share can be revoked.
      </SectionBody>
      <SectionBody>
        Your partner's shared notes arrive marked as theirs, never as yours. If either person chooses to include eligible shared context in a Series, a chapter can draw on both accounts. Keeping that context out is also a complete, supported setup.
      </SectionBody>
      <SectionBody>
        {/* The <em> is the `parable` charter's own frontmatter `description` VERBATIM (`shared/authorship/prompts/presets/stories/parable.mdx`). Requote it, don't restyle it. */}
        Some couples run it as a <strong>parable</strong>: a fiction Series where the two of you are characters in a wisdom-tale, and the week's small frictions come back as a traveler's encounter rather than a recap. Gnothi's own description of that charter is the pitch: <em>"An allegorical road country - waystations, crossings, and trials with a price named"</em>. It is a way of getting some distance on your own week, and a way of watching the two of you turn up as <strong>characters</strong> in it.
      </SectionBody>
    </Band>
  );
}

export default function Community() {
  // Band tones alternate with no repeat across a seam: dark · light · grey · light · dark · light · grey · light. `Matching` spends the page's one dark accent — it is prose-only (no `<Card>`, no `text.primary` span), so `SectionBody` flips the muted role to grey.300 for free.
  return (
    <SplashChrome active="community">
      <Hero />
      <SharingSeries />
      <Tiers />
      <Groups />
      <Matching />
      <Dnd />
      <Couples />
      <CtaBand
        color="light"
        title="Start with one shared tag"
        deck="Full-note sharing is free. Choose the tags, choose the recipient, and keep every Series source setting under your control."
      />
    </SplashChrome>
  );
}
